import { Inject, Injectable, InjectionToken } from '@angular/core';
import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from 'environments/environment';

export const FARO_INITIALIZE_FN = new InjectionToken<typeof initializeFaro>('FARO_INITIALIZE_FN', {
  providedIn: 'root',
  factory: () => initializeFaro
});

// Escapes regex metacharacters in apiURL (notably ".") before anchoring it — an unescaped "."
// matches ANY character, so "api.brightcities.city" would also match a look-alike domain that
// swaps the dot for something else. "(?:/|$)" requires the match to end at a path boundary, not
// just anywhere inside a longer hostname.
//
// Computed once here at module load, from environment.apiURL, NOT lazily inside init() like
// environment.monitoring.enabled is — harmless today (environment is fixed per build, never
// mutated at runtime in production), but if a test or future refactor starts swapping apiURL
// after this module loads, this regex won't see the new value.
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const API_TRACE_HEADER_URLS = [new RegExp(`^${escapeRegExp(environment.apiURL)}(?:/|$)`)];

@Injectable({ providedIn: 'root' })
export class FaroService {
  constructor(@Inject(FARO_INITIALIZE_FN) private initFaro: typeof initializeFaro) { }

  init(): void {
    if (!environment.monitoring.enabled) return;

    try {
      this.initFaro({
        url: environment.monitoring.collectorUrl,
        apiKey: environment.monitoring.apiKey,
        app: { name: 'bright-cities-web' },
        instrumentations: [
          ...getWebInstrumentations(),
          new TracingInstrumentation({
            instrumentationOptions: { propagateTraceHeaderCorsUrls: API_TRACE_HEADER_URLS }
          })
        ]
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Faro initialization failed; continuing without frontend telemetry.', error);
    }
  }
}
